import {
    GroupCollaborationEntityOwner, GroupCollaborationEntityType, GroupCollaborationTemplateFilter, GroupCollaborationWorkshopDuration, RequestStatus, SortOrder,
    WorkshopWidgetStatus, defaults, entities, getAPIMethod
} from 'typed/api';
import { ExtractAction, NetworkAction, action, entityActionSet, networkAction } from './utils';
import { download, getDateTimestamp, sanitizeFileName } from 'typed/utils';

interface WorkshopCreationOptions { templateID?: number | null; useTemplateCompany?: boolean; }
type AddWorkshop = NetworkAction<'ADD_WORKSHOP', { entity: entities.WorkshopReport; sync?: boolean; reloadAfterSync?: boolean; options: WorkshopCreationOptions; }, number>;
type AddAllWorkshops = NetworkAction<'ADD_ALL_WORKSHOPS', { entities: entities.WorkshopReport[]; sync?: boolean; reloadAfterSync?: boolean; options: WorkshopCreationOptions; }, number[]>;
type AddWorkshopTemplate = NetworkAction<'ADD_WORKSHOP_TEMPLATE', { entity: entities.WorkshopReport; sync?: boolean; reloadAfterSync?: boolean; }, number>;

type AddAllWorkshopParticipants = NetworkAction<'ADD_ALL_WORKSHOP_PARTICIPANTS', {
    parentID: number;
    entities: entities.WorkshopParticipant[];
    sync?: boolean;
    reloadAfterSync?: boolean;
    invite?: boolean;
    coverMessage?: string;
}, number[]>;

type AddAllWorkshopRatingPhaseSources = NetworkAction<
'ADD_ALL_WORKSHOP_RATING_PHASE_SOURCES',
{ grandGrandParentID: number; grandParentID: number; parentID: number; entities: entities.WorkshopRatingPhaseSourceReport[]; sync?: boolean; reloadAfterSync?: boolean; },
entities.WorkshopRatingPhaseSourceReport[]
>;

const workshopActionSet = entityActionSet(defaults.WorkshopReport, {
    loadAll: networkAction('LOAD_ALL_WORKSHOPS', getAPIMethod('Workshop', 'loadAll')),
    load: networkAction('LOAD_WORKSHOP', getAPIMethod('Workshop', 'load')),
    edit: networkAction('EDIT_WORKSHOP', getAPIMethod('Workshop', 'edit')),
    delete: networkAction('DELETE_WORKSHOP', getAPIMethod('Workshop', 'delete')),
    voidAll: action('VOID_ALL_WORKSHOPS')
});

export const
    { loadAll: loadAllSystemWorkshopTemplates } = entityActionSet(defaults.WorkshopReport, {
        loadAll: networkAction('LOAD_ALL_SYSTEM_WORKSHOP_TEMPLATES', getAPIMethod('Workshop', 'loadAllSystemTemplates'))
    }),

    workshops = {
        ...workshopActionSet,
        add: (entity: entities.WorkshopReport, sync = false, reloadAfterSync = false, options: WorkshopCreationOptions = {}): AddWorkshop =>
            async(dispatch: any) => {
                dispatch({
                    type: 'ADD_WORKSHOP',
                    payload: { entity, entityID: entity.ID, status: RequestStatus.UNSYNCED, ...options }
                });

                let entityID = entity.ID;

                if (sync) {
                    entityID = await dispatch(networkAction(
                        'ADD_WORKSHOP',
                        { entity, entityID: entity.ID, ...options },
                        getAPIMethod<number>('Workshop', 'add'),
                        entity,
                        options
                    ));
                }

                if (reloadAfterSync) {
                    return dispatch(workshopActionSet.load(entityID));
                }

                return entityID;
            },
        addAll: (entities: entities.WorkshopReport[], sync = false, reloadAfterSync = false, options: WorkshopCreationOptions = {}): AddAllWorkshops =>
            async(dispatch: any) => {
                dispatch({
                    type: 'ADD_ALL_WORKSHOPS',
                    payload: { entities, status: RequestStatus.UNSYNCED, ...options }
                });

                let entityIDs = entities.map(({ ID }) => ID);

                if (sync) {
                    entityIDs = await dispatch(networkAction(
                        'ADD_ALL_WORKSHOPS',
                        { entities, ...options },
                        getAPIMethod<number>('Workshop', 'addAllReports'),
                        entities,
                        options
                    ));
                }

                if (reloadAfterSync) {
                    dispatch(networkAction(
                        'LOAD_MANY_WORKSHOPS',
                        { entityIDs },
                        getAPIMethod<entities.WorkshopParticipantRole[]>('Workshop', 'loadMany'),
                        entityIDs
                    ));
                }

                return entityIDs;
            },
        addTemplate: (entity: entities.WorkshopReport, sync = false, reloadAfterSync = false): AddWorkshopTemplate =>
            async(dispatch: any) => {
                dispatch({
                    type: 'ADD_WORKSHOP_TEMPLATE',
                    payload: { entity, entityID: entity.ID, status: RequestStatus.UNSYNCED }
                });

                let entityID = entity.ID;

                if (sync) {
                    entityID = await dispatch(networkAction(
                        'ADD_WORKSHOP_TEMPLATE',
                        { entity, entityID: entity.ID },
                        getAPIMethod<number>('Workshop', 'addTemplate'),
                        entity
                    ));
                }

                if (reloadAfterSync && entityID > 0) {
                    return dispatch(workshopActionSet.load(entityID));
                }

                return entityID;
            },
        preview: (workshopID: number) =>
            networkAction('PREVIEW_WORKSHOP', { workshopID }, getAPIMethod<entities.WorkshopReport>('Workshop', 'preview'), workshopID),
        clearAllData: (entityID: number) =>
            networkAction('CLEAR_ALL_WORKSHOP_DATA', { entityID }, getAPIMethod<entities.WorkshopReport>('Workshop', 'clearWorkshopData'), entityID),
        clone: (settings: entities.WorkshopCloneSettings) =>
            networkAction('CLONE_WORKSHOP', { settings }, getAPIMethod<entities.WorkshopReport[]>('Workshop', 'clone'), settings),
        recreate: (entityID: number) =>
            networkAction('RECREATE_WORKSHOP', { entityID }, getAPIMethod('Workshop', 'recreateFromTemplate'), entityID),
        toggleMultiUserStatus: (entityID: number, isSingleUser: boolean) =>
            networkAction(
                'TOGGLE_WORKSHOP_MULTI_USER_STATUS',
                { entityID, isSingleUser },
                getAPIMethod<entities.WorkshopReport>('Workshop', 'toggleMultiUserStatus'),
                entityID,
                isSingleUser
            ),
        storeScreenshot: (entityID: number, image: entities.FileTransferHelperEntity) =>
            networkAction('STORE_WORKSHOP_SCREENSHOT', { entityID, image }, getAPIMethod<string>('Workshop', 'storeWorkshopScreenshot'), image, entityID),
        sendInvitations: (entityID: number, invitedUsers: number[], options: { coverMessage?: string; sendEmail?: boolean; } = {}) =>
            networkAction('SEND_WORKSHOP_INVITATIONS', { entityID, invitedUsers, ...options }, getAPIMethod('Workshop', 'sendInvitations'), invitedUsers, entityID, options),
        close: (entityID: number, publishResults: boolean, showParticipantProgress: boolean, createPublicLink: boolean, sendMessage: boolean, options: { coverMessage?: string; } = {}) =>
            networkAction(
                'CLOSE_WORKSHOP',
                { entityID, publishResults, showParticipantProgress, createPublicLink, sendMessage, options },
                getAPIMethod('Workshop', 'closeWorkshop'),
                entityID,
                publishResults,
                showParticipantProgress,
                createPublicLink,
                sendMessage,
                options
            ),
        markAsVisited: (entityID: number) => action('MARK_WORKSHOP_AS_VISITED', { entityID }),
        generateCustomBackgroundImage: (prompt: string) =>
            networkAction('GENERATE_CUSTOM_WORKSHOP_BACKGROUND_IMAGE', { prompt }, getAPIMethod<string>('Workshop', 'generateCustomBackgroundImage'), prompt),
        setCustomBackgroundImage: (base64Image: string) =>
            action('SET_CUSTOM_WORKSHOP_BACKGROUND_IMAGE', { base64Image }),
        clearTemporaryBackgroundImage: () => action('CLEAR_CUSTOM_WORKSHOP_BACKGROUND_IMAGE'),
        storeCustomBackgroundImage: (entityID: number, image: entities.FileTransferHelperEntity) =>
            networkAction('STORE_CUSTOM_WORKSHOP_BACKGROUND_IMAGE', { entityID, image }, getAPIMethod<string>('Workshop', 'storeCustomBackgroundImage'), image, entityID),
        convertToTemplate: (entityID: number, createNew: boolean) =>
            networkAction('CONVERT_WORKSHOP_TO_TEMPLATE', { entityID, createNew }, getAPIMethod<entities.WorkshopReport>('Workshop', 'convertToTemplate'), entityID, createNew),
        downloadWorkshopDataZip: (entityID: number, workshopName = '') =>
            async(dispatch: any) => {
                const response = await dispatch(networkAction(
                    'DOWNLOAD_WORKSHOP_DATA_ZIP',
                    { entityID },
                    getAPIMethod('Workshop', 'createWorkshopDataZip'),
                    entityID
                ));

                if (response) {
                    const linkSource = `data:text/plain;base64,${response}`;
                    download(linkSource, sanitizeFileName(`${workshopName || 'workshop_data'}_${getDateTimestamp()}.zip`));
                }
            }
    },

    workshopBackgroundImages = {
        ...entityActionSet(defaults.WorkshopBackgroundImage, {
            loadAll: networkAction('LOAD_ALL_WORKSHOP_BACKGROUND_IMAGES', getAPIMethod('WorkshopBackgroundImage', 'loadAll'))
        }),
        loadBase64Image: (entityID: number) =>
            networkAction('LOAD_WORKSHOP_BACKGROUND_IMAGE_AS_BASE64_STRING', { entityID }, getAPIMethod<string>('WorkshopBackgroundImage', 'loadImageAsBase64String'), entityID)
    },


    workshopParticipantRoles = {
        loadMany: (parentID: number, entityIDs: number[]) =>
            networkAction(
                'LOAD_MANY_WORKSHOP_PARTICIPANT_ROLES',
                { parentID, entityIDs },
                getAPIMethod<entities.WorkshopParticipantRole[]>('WorkshopParticipantRole', 'loadMany'),
                entityIDs
            ),
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopParticipantRole, {
            add: networkAction('ADD_WORKSHOP_PARTICIPANT_ROLE', getAPIMethod('WorkshopParticipantRole', 'add')),
            edit: networkAction('EDIT_WORKSHOP_PARTICIPANT_ROLE', getAPIMethod('WorkshopParticipantRole', 'edit')),
            delete: networkAction('DELETE_WORKSHOP_PARTICIPANT_ROLE', getAPIMethod('WorkshopParticipantRole', 'delete'))
        })
    },

    workshopParticipants = {
        loadMany: (parentID: number, entityIDs: number[]) =>
            networkAction(
                'LOAD_MANY_WORKSHOP_PARTICIPANTS',
                { parentID, entityIDs },
                getAPIMethod<entities.WorkshopParticipant[]>('WorkshopParticipant', 'loadMany'),
                entityIDs
            ),
        search: (templates: Partial<entities.WorkshopParticipant>[]) =>
            networkAction('SEARCH_WORKSHOP_PARTICIPANTS', { templates }, getAPIMethod<entities.WorkshopParticipant[]>('WorkshopParticipant', 'search'), templates),
        addAll: (parentID: number, entities: entities.WorkshopParticipant[], sync = false, reloadAfterSync = false, invite = false, coverMessage = '', sendEmail = true): AddAllWorkshopParticipants =>
            async(dispatch: any) => {
                let entityIDs = entities.map(({ ID }) => ID);

                dispatch({
                    type: 'ADD_ALL_WORKSHOP_PARTICIPANTS',
                    payload: { parentID, entities, entityIDs, status: RequestStatus.UNSYNCED, invite, coverMessage, sendEmail }
                });

                if (sync) {
                    entityIDs = await dispatch(networkAction(
                        'ADD_ALL_WORKSHOP_PARTICIPANTS',
                        { parentID, entities, entityIDs, invite, coverMessage, sendEmail },
                        getAPIMethod<number[]>('WorkshopParticipant', 'addAll'),
                        entities,
                        { invite, coverMessage, sendEmail }
                    ));
                }

                if (reloadAfterSync) {
                    return dispatch(workshopParticipants.loadMany(parentID, entityIDs));
                }

                return entityIDs;
            },
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopParticipant, {
            add: networkAction('ADD_WORKSHOP_PARTICIPANT', getAPIMethod('WorkshopParticipant', 'add')),
            edit: networkAction('EDIT_WORKSHOP_PARTICIPANT', getAPIMethod('WorkshopParticipant', 'edit')),
            deleteAll: networkAction('DELETE_ALL_WORKSHOP_PARTICIPANTS', getAPIMethod('WorkshopParticipant', 'deleteAll'))
        }),
        touch: (parentID: number, participantID: number) =>
            networkAction('TOUCH_WORKSHOP_PARTICIPANT', { parentID, entityID: participantID }, getAPIMethod('WorkshopParticipant', 'touch'), participantID)
    },

    workshopVideoCalls = entityActionSet(defaults.WorkshopReport, defaults.WorkshopVideoCall, {
        load: networkAction('LOAD_WORKSHOP_VIDEO_CALL', getAPIMethod('WorkshopVideoCall', 'load')),
        add: networkAction('ADD_WORKSHOP_VIDEO_CALL', getAPIMethod('WorkshopVideoCall', 'add')),
        edit: networkAction('EDIT_WORKSHOP_VIDEO_CALL', getAPIMethod('WorkshopVideoCall', 'edit')),
        delete: networkAction('DELETE_WORKSHOP_VIDEO_CALL', getAPIMethod('WorkshopVideoCall', 'delete'))
    }),


    workshopPhases = {
        loadAll: (parentID: number) =>
            networkAction('LOAD_WORKSHOP_PHASES', { parentID }, getAPIMethod<entities.WorkshopPhaseReport[]>('WorkshopPhase', 'loadWorkshopPhases'), parentID),
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseReport, {
            load: networkAction('LOAD_WORKSHOP_PHASE', getAPIMethod('WorkshopPhase', 'load')),
            add: networkAction('ADD_WORKSHOP_PHASE', getAPIMethod('WorkshopPhase', 'add')),
            edit: networkAction('EDIT_WORKSHOP_PHASE', getAPIMethod('WorkshopPhase', 'edit')),
            editAll: networkAction('EDIT_ALL_WORKSHOP_PHASES', getAPIMethod('WorkshopPhase', 'editAll')),
            delete: networkAction('DELETE_WORKSHOP_PHASE', getAPIMethod('WorkshopPhase', 'delete'))
        }),
        clone: (parentID: number, entityID: number) =>
            networkAction('CLONE_WORKSHOP_PHASE', { parentID, entityID }, getAPIMethod<entities.WorkshopPhaseReport[]>('WorkshopPhase', 'clone'), entityID),
        changePublicationStatus: (parentID: number, entityID: number, published: boolean) =>
            networkAction(
                'CHANGE_WORKSHOP_PHASE_PUBLICATION_STATUS',
                { parentID, entityID, published },
                getAPIMethod<entities.WorkshopPhaseReport>('WorkshopPhase', 'changePhasePublicationStatus'),
                entityID,
                published
            ),
        storeCustomBackgroundImage: (parentID: number, entityID: number, image: entities.FileTransferHelperEntity) =>
            networkAction(
                'STORE_CUSTOM_WORKSHOP_PHASE_BACKGROUND_IMAGE',
                { parentID, entityID, image },
                getAPIMethod<string>('WorkshopPhase', 'storeCustomBackgroundImage'),
                image,
                entityID
            )
    },

    workshopPhaseParticipantRoles = {
        loadMany: (grandParentID: number, parentID: number, entityIDs: number[]) =>
            networkAction(
                'LOAD_MANY_WORKSHOP_PHASE_PARTICIPANT_ROLES',
                { grandParentID, parentID, entityIDs },
                getAPIMethod<entities.WorkshopPhaseParticipantRole[]>('WorkshopPhaseParticipantRole', 'loadMany'),
                entityIDs
            ),
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseReport, defaults.WorkshopPhaseParticipantRole, {
            add: networkAction('ADD_WORKSHOP_PHASE_PARTICIPANT_ROLE', getAPIMethod('WorkshopPhaseParticipantRole', 'add')),
            edit: networkAction('EDIT_WORKSHOP_PHASE_PARTICIPANT_ROLE', getAPIMethod('WorkshopPhaseParticipantRole', 'edit')),
            deleteAll: networkAction('DELETE_ALL_WORKSHOP_PHASE_PARTICIPANT_ROLES', getAPIMethod('WorkshopPhaseParticipantRole', 'deleteAll')),
            delete: networkAction('DELETE_WORKSHOP_PHASE_PARTICIPANT_ROLE', getAPIMethod('WorkshopPhaseParticipantRole', 'delete'))
        })
    },

    { loadAll: loadAllWorkshopPhaseContentRowLayouts } = entityActionSet(defaults.WorkshopPhaseContentRowLayout, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_PHASE_CONTENT_ROW_LAYOUTS', getAPIMethod('WorkshopPhaseContentRowLayout', 'loadAll'))
    }),

    workshopPhaseContentRows = {
        loadAll: () => networkAction('LOAD_ALL_WORKSHOP_PHASE_CONTENT_ROWS', getAPIMethod('WorkshopPhaseContentRow', 'loadAll')),
        loadWorkshopContent: (workshopIDs: number[]) =>
            networkAction(
                'LOAD_WORKSHOP_PHASE_CONTENT_ROWS',
                { workshopIDs },
                getAPIMethod<entities.WorkshopPhaseContentRowReport[]>('WorkshopPhaseContentRow', 'loadWorkshopContent'),
                workshopIDs
            ),
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, {
            load: networkAction('LOAD_WORKSHOP_PHASE_CONTENT_ROW', getAPIMethod('WorkshopPhaseContentRow', 'load')),
            add: networkAction('ADD_WORKSHOP_PHASE_CONTENT_ROW', getAPIMethod('WorkshopPhaseContentRow', 'add')),
            edit: networkAction('EDIT_WORKSHOP_PHASE_CONTENT_ROW', getAPIMethod('WorkshopPhaseContentRow', 'edit')),
            delete: networkAction('DELETE_WORKSHOP_PHASE_CONTENT_ROW', getAPIMethod('WorkshopPhaseContentRow', 'delete'))
        })
    },

    { loadAll: loadAllWorkshopCanvasItemTypes } = entityActionSet(defaults.WorkshopCanvasItemType, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_CANVAS_ITEM_TYPES', getAPIMethod('WorkshopCanvasItemType', 'loadAll'))
    }),

    workshopCanvasItems = entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseReport, defaults.WorkshopCanvasItem, {
        load: networkAction('LOAD_WORKSHOP_CANVAS_ITEM', getAPIMethod('WorkshopCanvasItem', 'load')),
        add: networkAction('ADD_WORKSHOP_CANVAS_ITEM', getAPIMethod('WorkshopCanvasItem', 'add')),
        edit: networkAction('EDIT_WORKSHOP_CANVAS_ITEM', getAPIMethod('WorkshopCanvasItem', 'edit')),
        delete: networkAction('DELETE_WORKSHOP_CANVAS_ITEM', getAPIMethod('WorkshopCanvasItem', 'delete'))
    }),


    { loadAll: loadAllWorkshopWidgetTypes } = entityActionSet(defaults.WorkshopWidgetType, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_WIDGET_TYPES', getAPIMethod('WorkshopWidgetType', 'loadAll'))
    }),

    { loadAll: loadAllWorkshopWidgetStatuses } = entityActionSet(defaults.WorkshopWidgetStatus, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_WIDGET_STATUSES', getAPIMethod('WorkshopWidgetStatus', 'loadAll'))
    }),

    { loadAll: loadAllWorkshopExternalContentTypes } = entityActionSet(defaults.WorkshopExternalContentType, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_EXTERNAL_CONTENT_TYPES', getAPIMethod('WorkshopExternalContentType', 'loadAll'))
    }),

    { loadAll: loadAllWorkshopPhaseContentCreationRules } = entityActionSet(defaults.WorkshopPhaseContentCreationRule, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_PHASE_CONTENT_CREATION_RULES', getAPIMethod('WorkshopPhaseContentCreationRule', 'loadAll'))
    }),

    workshopWidgets = {
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, {
            load: networkAction('LOAD_WORKSHOP_WIDGET', getAPIMethod('WorkshopWidget', 'load')),
            add: networkAction('ADD_WORKSHOP_WIDGET', getAPIMethod('WorkshopWidget', 'add')),
            edit: networkAction('EDIT_WORKSHOP_WIDGET', getAPIMethod('WorkshopWidget', 'edit')),
            delete: networkAction('DELETE_WORKSHOP_WIDGET', getAPIMethod('WorkshopWidget', 'delete')),
            voidAll: action('VOID_ALL_WORKSHOP_WIDGETS')
        }),
        generateWidgetContents: (
            grandParentID: number,
            parentID: number,
            entityID: number,
            categoryCreationRuleID: number,
            categoryCreationWidgetID: number | null,
            itemCreationRuleID: number,
            categoryLimit: number | null
        ) =>
            networkAction(
                'GENERATE_WORKSHOP_WIDGET_CONTENTS',
                { grandParentID, parentID, entityID, categoryCreationRuleID, categoryCreationWidgetID, itemCreationRuleID, conversionLimitOverride: categoryLimit },
                getAPIMethod<entities.WorkshopWidgetReport>('WorkshopWidget', 'generateWidgetContents'),
                entityID,
                categoryCreationRuleID,
                itemCreationRuleID,
                { categoryCreationWidgetID, conversionLimitOverride: categoryLimit }
            ),
        generateDetailedWidgetContents: (entityID: number, settings: entities.WorkshopStrategyImportSettings, overrideExisting: boolean) =>
            networkAction(
                'GENERATE_DETAILED_WORKSHOP_WIDGET_CONTENTS',
                { entityID, settings, overrideExisting },
                getAPIMethod<entities.WorkshopWidgetReport>('WorkshopWidget', 'generateDetailedWidgetContents'),
                settings,
                entityID,
                overrideExisting
            ),
        generateIdeas: (entityID: number, answers: entities.WorkshopWidgetIdeaGenerationThemeAnswer[]) =>
            networkAction(
                'GENERATE_WORKSHOP_WIDGET_IDEAS',
                { entityID, answers },
                getAPIMethod<string[]>('WorkshopWidget', 'generateWidgetIdeas'),
                answers,
                entityID
            ),
        dropIdeas: (entityID: number) => action('DROP_WORKSHOP_WIDGET_IDEAS', { entityID }),
        setStatus: (grandParentID: number, parentID: number, entityID: number, statusID: WorkshopWidgetStatus) =>
            networkAction(
                'SET_WORKSHOP_WIDGET_STATUS',
                { grandParentID, parentID, entityID, statusID },
                getAPIMethod<entities.WorkshopWidgetReport>('WorkshopWidget', 'setStatus'),
                entityID,
                statusID
            ),
        calculateRelativeItemRatings: (grandParentID: number, parentID: number, entityID: number) =>
            networkAction(
                'CALCULATE_WORKSHOP_WIDGET_RELATIVE_ITEM_RATINGS',
                { grandParentID, parentID, entityID },
                getAPIMethod<entities.WorkshopPhaseReport>('WorkshopWidget', 'calculateRelativeItemRatings'),
                entityID
            ),
        testUrl: (url: string) => networkAction('TEST_WORKSHOP_WIDGET_EXTERNAL_CONTENT_URL', { url }, getAPIMethod<string>('WorkshopWidget', 'testEmbedUrl'), url),
        dropUrlTestResults: () => action('DROP_WORKSHOP_WIDGET_EXTERNAL_CONTENT_TEST_RESULTS'),
        downloadFile: (grandParentID: number, parentID: number, entityID: number, fileName: string, mimeType: string) =>
            async(dispatch: any) => {
                const payload = { grandParentID, parentID, entityID };
                dispatch({ type: 'DOWNLOAD_WORKSHOP_WIDGET_EXTERNAL_CONTENT_FILE', payload: { ...payload, status: RequestStatus.PENDING } });

                const request = dispatch(networkAction('DOWNLOAD_WORKSHOP_WIDGET_EXTERNAL_CONTENT_FILE', payload, getAPIMethod<string>('WorkshopWidget', 'downloadFile'), entityID));
                request.then((response: string) => {
                    if (response) {
                        const
                            linkSource = `data:${mimeType};base64,${response}`,
                            linkElement = document.createElement('a');

                        linkElement.setAttribute('href', linkSource);
                        linkElement.setAttribute('download', fileName);
                        linkElement.click();
                    }
                });

                return await request;
            }
    },

    workshopWidgetParticipantStatuses = {
        loadMany: (grandGrandParentID: number, grandParentID: number, parentID: number, entityIDs: number[]) =>
            networkAction(
                'LOAD_MANY_WORKSHOP_WIDGET_PARTICIPANT_STATUSES',
                { grandGrandParentID, grandParentID, parentID, entityIDs },
                getAPIMethod<entities.WorkshopWidgetParticipantStatus[]>('WorkshopWidgetParticipantStatus', 'loadMany'),
                entityIDs
            ),
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopWidgetParticipantStatus, {
            add: networkAction('ADD_WORKSHOP_WIDGET_PARTICIPANT_STATUS', getAPIMethod('WorkshopWidgetParticipantStatus', 'add')),
            delete: networkAction('DELETE_WORKSHOP_WIDGET_PARTICIPANT_STATUS', getAPIMethod('WorkshopWidgetParticipantStatus', 'delete'))
        }),
        clearWidgetStatuses: (grandGrandParentID: number, widgetIDs: number[]) =>
            networkAction(
                'CLEAR_WORKSHOP_WIDGET_PARTICIPANT_STATUSES',
                { grandGrandParentID, widgetIDs },
                getAPIMethod('WorkshopWidgetParticipantStatus', 'clearWidgetParticipantProgress'),
                widgetIDs
            )
    },


    workshopItemCategories = {
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopItemCategory, {
            addAll: networkAction('ADD_ALL_WORKSHOP_ITEM_CATEGORIES', getAPIMethod('WorkshopItemCategory', 'addAll')),
            editAll: networkAction('EDIT_ALL_WORKSHOP_ITEM_CATEGORIES', getAPIMethod('WorkshopItemCategory', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_WORKSHOP_ITEM_CATEGORIES', getAPIMethod('WorkshopItemCategory', 'deleteAll'))
        }),
        categorizeItems: (grandGrandParentID: number, grandParentID: number, parentID: number) =>
            networkAction(
                'CATEGORIZE_WORKSHOP_WIDGET_ITEMS',
                { grandGrandParentID, grandParentID, parentID },
                getAPIMethod<entities.WorkshopItemCategory[]>('WorkshopItemCategory', 'categorizeItems'),
                parentID
            ),
        extractCategoryItemsFromSources: (grandGrandParentID: number, grandParentID: number, parentID: number, entityID: number) =>
            networkAction(
                'EXTRACT_WORKSHOP_ITEM_CATEGORY_ITEMS_FROM_SOURCES',
                { grandGrandParentID, grandParentID, parentID, entityID },
                getAPIMethod<entities.WorkshopWidgetReport>('WorkshopItemCategory', 'extractCategoryItemsFromSources'),
                entityID
            )
    },

    workshopItemCategoryConnections = {
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopItemCategoryConnection, {
            addAll: networkAction('ADD_ALL_WORKSHOP_ITEM_CATEGORY_CONNECTIONS', getAPIMethod('WorkshopItemCategoryConnection', 'addAll')),
            editAll: networkAction('EDIT_ALL_WORKSHOP_ITEM_CATEGORY_CONNECTIONS', getAPIMethod('WorkshopItemCategoryConnection', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_WORKSHOP_ITEM_CATEGORY_CONNECTIONS', getAPIMethod('WorkshopItemCategoryConnection', 'deleteAll'))
        }),
        touch: (grandGrandParentID: number, grandParentID: number, parentID: number, entityID: number) =>
            networkAction(
                'TOUCH_WORKSHOP_ITEM_CATEGORY_CONNECTION',
                { grandGrandParentID, grandParentID, parentID, entityID },
                getAPIMethod('WorkshopItemCategoryConnection', 'touch'),
                entityID
            )
    },


    { loadAll: loadAllWorkshopPrioritizationVotingTypes } = entityActionSet(defaults.WorkshopPrioritizationVotingType, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_PRIORITIZATION_VOTING_TYPES', getAPIMethod('WorkshopPrioritizationVotingType', 'loadAll'))
    }),


    workshopQuestionnaireFilters = entityActionSet(
        defaults.WorkshopReport,
        defaults.WorkshopPhaseContentRowReport,
        defaults.WorkshopWidgetReport,
        defaults.WorkshopQuestionnaireFilter,
        {
            add: networkAction('ADD_WORKSHOP_QUESTIONNAIRE_FILTER', getAPIMethod('WorkshopQuestionnaireFilter', 'add')),
            edit: networkAction('EDIT_WORKSHOP_QUESTIONNAIRE_FILTER', getAPIMethod('WorkshopQuestionnaireFilter', 'edit'))
        }
    ),

    { loadAll: loadAllWorkshopQuestionnaireQuestionTypes } = entityActionSet(defaults.WorkshopQuestionnaireQuestionType, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_QUESTIONNAIRE_QUESTION_TYPES', getAPIMethod('WorkshopQuestionnaireQuestionType', 'loadAll'))
    }),

    workshopQuestionnaireQuestions = {
        ...entityActionSet(
            defaults.WorkshopReport,
            defaults.WorkshopPhaseContentRowReport,
            defaults.WorkshopWidgetReport,
            defaults.WorkshopQuestionnaireQuestionReport,
            {
                load: networkAction('LOAD_WORKSHOP_QUESTIONNAIRE_QUESTION', getAPIMethod('WorkshopQuestionnaireQuestion', 'load')),
                editAll: networkAction('EDIT_ALL_WORKSHOP_QUESTIONNAIRE_QUESTIONS', getAPIMethod('WorkshopQuestionnaireQuestion', 'editAll')),
                deleteAll: networkAction('DELETE_ALL_WORKSHOP_QUESTIONNAIRE_QUESTIONS', getAPIMethod('WorkshopQuestionnaireQuestion', 'deleteAll'))
            }
        ),
        addAll: (grandGrandParentID: number, grandParentID: number, parentID: number, entities: entities.WorkshopQuestionnaireQuestionReport[]) =>
            async(dispatch: any) => {
                const payload = { grandGrandParentID, grandParentID, parentID, entities, entityIDs: entities.map(({ ID }) => ID) };
                dispatch({ type: 'ADD_ALL_WORKSHOP_QUESTIONNAIRE_QUESTIONS', payload: { ...payload, status: RequestStatus.UNSYNCED } });

                return await dispatch(networkAction(
                    'ADD_ALL_WORKSHOP_QUESTIONNAIRE_QUESTIONS',
                    payload,
                    getAPIMethod<entities.WorkshopQuestionnaireQuestionReport[]>('WorkshopQuestionnaireQuestion', 'addAll'),
                    entities
                ));
            }
    },

    workshopQuestionnaireOptions = entityActionSet(
        defaults.WorkshopReport,
        defaults.WorkshopPhaseContentRowReport,
        defaults.WorkshopWidgetReport,
        defaults.WorkshopQuestionnaireOption,
        {
            addAll: networkAction('ADD_ALL_WORKSHOP_QUESTIONNAIRE_OPTIONS', getAPIMethod('WorkshopQuestionnaireOption', 'addAll')),
            editAll: networkAction('EDIT_ALL_WORKSHOP_QUESTIONNAIRE_OPTIONS', getAPIMethod('WorkshopQuestionnaireOption', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_WORKSHOP_QUESTIONNAIRE_OPTIONS', getAPIMethod('WorkshopQuestionnaireOption', 'deleteAll'))
        }
    ),

    workshopQuestionnaireResponses = entityActionSet(
        defaults.WorkshopPhaseContentRowReport,
        defaults.WorkshopWidgetReport,
        defaults.WorkshopQuestionnaireQuestionReport,
        defaults.WorkshopQuestionnaireResponse,
        {
            add: networkAction('ADD_WORKSHOP_QUESTIONNAIRE_RESPONSE', getAPIMethod('WorkshopQuestionnaireResponse', 'add')),
            edit: networkAction('EDIT_WORKSHOP_QUESTIONNAIRE_RESPONSE', getAPIMethod('WorkshopQuestionnaireResponse', 'edit'))
        }
    ),


    { loadAll: loadAllWorkshopResultPresentationTypes } = entityActionSet(defaults.WorkshopResultPresentationType, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_RESULT_PRESENTATION_TYPES', getAPIMethod('WorkshopResultPresentationType', 'loadAll'))
    }),

    workshopResultPhaseSources = entityActionSet(
        defaults.WorkshopReport,
        defaults.WorkshopPhaseContentRowReport,
        defaults.WorkshopWidgetReport,
        defaults.WorkshopResultPhaseSource,
        {
            addAll: networkAction('ADD_ALL_WORKSHOP_RESULT_PHASE_SOURCES', getAPIMethod('WorkshopResultPhaseSource', 'addAll')),
            editAll: networkAction('EDIT_ALL_WORKSHOP_RESULT_PHASE_SOURCES', getAPIMethod('WorkshopResultPhaseSource', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_WORKSHOP_RESULT_PHASE_SOURCES', getAPIMethod('WorkshopResultPhaseSource', 'deleteAll'))
        }
    ),

    workshopItems = entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopItem, {
        load: networkAction('LOAD_WORKSHOP_ITEM', getAPIMethod('WorkshopItem', 'load')),
        add: networkAction('ADD_WORKSHOP_ITEM', getAPIMethod('WorkshopItem', 'add')),
        addAll: networkAction('ADD_ALL_WORKSHOP_ITEMS', getAPIMethod('WorkshopItem', 'addAll')),
        edit: networkAction('EDIT_WORKSHOP_ITEM', getAPIMethod('WorkshopItem', 'edit')),
        editAll: networkAction('EDIT_ALL_WORKSHOP_ITEMS', getAPIMethod('WorkshopItem', 'editAll')),
        delete: networkAction('DELETE_WORKSHOP_ITEM', getAPIMethod('WorkshopItem', 'delete')),
        deleteAll: networkAction('DELETE_ALL_WORKSHOP_ITEMS', getAPIMethod('WorkshopItem', 'deleteAll'))
    }),

    { loadAll: loadAllWorkshopVersionHistoryChangeTypes } = entityActionSet(defaults.WorkshopVersionHistoryChangeType, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_VERSION_HISTORY_CHANGE_TYPES', getAPIMethod('WorkshopVersionHistoryChangeType', 'loadAll'))
    }),

    loadWorkshopItemHistory = (workshopID: number) =>
        networkAction('LOAD_WORKSHOP_ITEM_HISTORY', { entityID: workshopID }, getAPIMethod('WorkshopItemHistory', 'loadWorkshopItemHistory'), workshopID),


    { loadAll: loadAllWorkshopRatingPhaseWeightedLevels } = entityActionSet(defaults.WorkshopRatingPhaseWeightedLevel, {
        loadAll: networkAction('LOAD_ALL_WORKSHOP_RATING_PHASE_WEIGHTED_LEVELS', getAPIMethod('WorkshopRatingPhaseWeightedLevel', 'loadAll'))
    }),

    workshopRatingPhaseSources = {
        loadMany: (grandGrandParentID: number, grandParentID: number, parentID: number, entityIDs: number[]) =>
            networkAction(
                'LOAD_MANY_WORKSHOP_RATING_PHASE_SOURCES',
                { grandGrandParentID, grandParentID, parentID, entityIDs },
                getAPIMethod<entities.WorkshopRatingPhaseSourceReport>('WorkshopRatingPhaseSource', 'loadMany'),
                entityIDs
            ),
        addAll: (grandGrandParentID: number, grandParentID: number, parentID: number, entities: entities.WorkshopRatingPhaseSourceReport[], sync = false, reloadAfterSync = false): AddAllWorkshopRatingPhaseSources =>
            async(dispatch: any) => {
                let entityIDs = entities.map(({ ID }) => ID);

                dispatch({
                    type: 'ADD_ALL_WORKSHOP_RATING_PHASE_SOURCES',
                    payload: { grandGrandParentID, grandParentID, parentID, entities, entityIDs, status: RequestStatus.UNSYNCED }
                });

                if (sync) {
                    entityIDs = await dispatch(networkAction(
                        'ADD_ALL_WORKSHOP_RATING_PHASE_SOURCES',
                        { grandGrandParentID, grandParentID, parentID, entities, entityIDs },
                        getAPIMethod<number>('WorkshopRatingPhaseSource', 'addAll'),
                        entities
                    ));
                }

                if (reloadAfterSync) {
                    return dispatch(workshopRatingPhaseSources.loadMany(grandGrandParentID, grandParentID, parentID, entityIDs));
                }

                return entityIDs;
            },
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopRatingPhaseSourceReport, {
            editAll: networkAction('EDIT_ALL_WORKSHOP_RATING_PHASE_SOURCES', getAPIMethod('WorkshopRatingPhaseSource', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_WORKSHOP_RATING_PHASE_SOURCES', getAPIMethod('WorkshopRatingPhaseSource', 'deleteAll'))
        })
    },

    workshopRatingPhaseCategoryWeights = entityActionSet(
        defaults.WorkshopReport,
        defaults.WorkshopPhaseContentRowReport,
        defaults.WorkshopWidgetReport,
        defaults.WorkshopRatingPhaseCategoryWeight,
        {
            addAll: networkAction('ADD_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS', getAPIMethod('WorkshopRatingPhaseCategoryWeight', 'addAll')),
            editAll: networkAction('EDIT_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS', getAPIMethod('WorkshopRatingPhaseCategoryWeight', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS', getAPIMethod('WorkshopRatingPhaseCategoryWeight', 'deleteAll'))
        }
    ),

    workshopItemRatings = {
        ...entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopItemRating, {
            add: networkAction('ADD_WORKSHOP_ITEM_RATING', getAPIMethod('WorkshopItemRating', 'add')),
            edit: networkAction('EDIT_WORKSHOP_ITEM_RATING', getAPIMethod('WorkshopItemRating', 'edit')),
            delete: networkAction('DELETE_WORKSHOP_ITEM_RATING', getAPIMethod('WorkshopItemRating', 'delete'))
        }),
        clearPhaseRatings: (workshopID: number, phaseID: number) =>
            networkAction(
                'CLEAR_WORKSHOP_ITEM_RATINGS',
                { grandParentID: workshopID, parentID: phaseID },
                getAPIMethod('WorkshopItemRating', 'searchAndDelete'),
                { PhaseID: phaseID }
            )
    },

    loadWorkshopItemRatingHistory = (workshopID: number) =>
        networkAction('LOAD_WORKSHOP_ITEM_RATING_HISTORY', { entityID: workshopID }, getAPIMethod('WorkshopItemRatingHistory', 'loadWorkshopItemRatingHistory'), workshopID),

    workshopPhaseGridBlocks = entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopPhaseGridBlock, {
        addAll: networkAction('ADD_ALL_WORKSHOP_PHASE_GRID_BLOCKS', getAPIMethod('WorkshopPhaseGridBlock', 'addAll')),
        edit: networkAction('EDIT_WORKSHOP_PHASE_GRID_BLOCK', getAPIMethod('WorkshopPhaseGridBlock', 'edit')),
        editAll: networkAction('EDIT_ALL_WORKSHOP_PHASE_GRID_BLOCKS', getAPIMethod('WorkshopPhaseGridBlock', 'editAll'))
    }),

    workshopPhaseComparisonAxes = entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopPhaseComparisonAxis, {
        addAll: networkAction('ADD_ALL_WORKSHOP_PHASE_COMPARISON_AXES', getAPIMethod('WorkshopPhaseComparisonAxis', 'addAll')),
        editAll: networkAction('EDIT_ALL_WORKSHOP_PHASE_COMPARISON_AXES', getAPIMethod('WorkshopPhaseComparisonAxis', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_WORKSHOP_PHASE_COMPARISON_AXES', getAPIMethod('WorkshopPhaseComparisonAxis', 'deleteAll'))
    }),

    workshopPhaseComparisonDimensions = entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopPhaseComparisonDimension, {
        addAll: networkAction('ADD_ALL_WORKSHOP_PHASE_COMPARISON_DIMENSIONS', getAPIMethod('WorkshopPhaseComparisonDimension', 'addAll')),
        editAll: networkAction('EDIT_ALL_WORKSHOP_PHASE_COMPARISON_DIMENSIONS', getAPIMethod('WorkshopPhaseComparisonDimension', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_WORKSHOP_PHASE_COMPARISON_DIMENSIONS', getAPIMethod('WorkshopPhaseComparisonDimension', 'deleteAll'))
    }),

    workshopWidgetIdeaGenerationThemes = entityActionSet(defaults.WorkshopReport, defaults.WorkshopPhaseContentRowReport, defaults.WorkshopWidgetReport, defaults.WorkshopWidgetIdeaGenerationTheme, {
        addAll: networkAction('ADD_ALL_WORKSHOP_WIDGET_IDEA_GENERATION_THEMES', getAPIMethod('WorkshopWidgetIdeaGenerationTheme', 'addAll')),
        editAll: networkAction('EDIT_ALL_WORKSHOP_WIDGET_IDEA_GENERATION_THEMES', getAPIMethod('WorkshopWidgetIdeaGenerationTheme', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_WORKSHOP_WIDGET_IDEA_GENERATION_THEMES', getAPIMethod('WorkshopWidgetIdeaGenerationTheme', 'deleteAll'))
    }),


    workspaceMindmaps = {
        ...entityActionSet(defaults.WorkspaceMindmapReport, {
            loadAll: networkAction('LOAD_ALL_WORKSPACE_MINDMAPS', getAPIMethod('WorkspaceMindmap', 'loadAll')),
            load: networkAction('LOAD_WORKSPACE_MINDMAP', getAPIMethod('WorkspaceMindmap', 'load')),
            add: networkAction('ADD_WORKSPACE_MINDMAP', getAPIMethod('WorkspaceMindmap', 'add')),
            edit: networkAction('EDIT_WORKSPACE_MINDMAP', getAPIMethod('WorkspaceMindmap', 'edit')),
            delete: networkAction('DELETE_WORKSPACE_MINDMAP', getAPIMethod('WorkspaceMindmap', 'delete')),
            voidAll: action('VOID_ALL_WORKSPACE_MINDMAPS')
        }),
        clone: (entityID: number, name: string) =>
            networkAction('CLONE_WORKSPACE_MINDMAP', { entityID, name }, getAPIMethod<entities.WorkspaceMindmapReport>('WorkspaceMindmap', 'clone'), entityID, name),
        storeScreenshot: (entityID: number, image: entities.FileTransferHelperEntity) =>
            networkAction('STORE_WORKSPACE_MINDMAP_SCREENSHOT', { entityID, image }, getAPIMethod<string>('WorkspaceMindmap', 'storeMindmapScreenshot'), image, entityID)
    },

    workspaceMindmapNodeTypes = {
        loadMindmapNodeTypes: (parentID: number, options: { translate?: boolean; } = {}) =>
            networkAction(
                'LOAD_ALL_WORKSPACE_MINDMAP_NODE_TYPES',
                { parentID, ...options },
                getAPIMethod<entities.WorkspaceMindmapNodeType[]>('WorkspaceMindmapNodeType', 'loadMindmapNodeTypes'),
                parentID,
                options
            ),
        ...entityActionSet(defaults.WorkspaceMindmapReport, defaults.WorkspaceMindmapNodeType, {
            addAll: networkAction('ADD_ALL_WORKSPACE_MINDMAP_NODE_TYPES', getAPIMethod('WorkspaceMindmapNodeType', 'addAll')),
            editAll: networkAction('EDIT_ALL_WORKSPACE_MINDMAP_NODE_TYPES', getAPIMethod('WorkspaceMindmapNodeType', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_WORKSPACE_MINDMAP_NODE_TYPES', getAPIMethod('WorkspaceMindmapNodeType', 'deleteAll'))
        })
    },

    workspaceMindmapNodes = {
        loadMindmapSlice: (parentID: number, options: { startID?: number; maxCount?: number; slicingOrder?: SortOrder; } = {}) =>
            networkAction('LOAD_WORKSPACE_MINDMAP_NODE_SLICE', { parentID, ...options }, getAPIMethod('WorkspaceMindmapNode', 'loadMindmapSlice'), parentID, options),
        ...entityActionSet(defaults.WorkspaceMindmapReport, defaults.WorkspaceMindmapNode, {
            load: networkAction('LOAD_WORKSPACE_MINDMAP_NODE', getAPIMethod('WorkspaceMindmapNode', 'load')),
            add: networkAction('ADD_WORKSPACE_MINDMAP_NODE', getAPIMethod('WorkspaceMindmapNode', 'add')),
            edit: networkAction('EDIT_WORKSPACE_MINDMAP_NODE', getAPIMethod('WorkspaceMindmapNode', 'edit')),
            deleteAll: networkAction('DELETE_ALL_WORKSPACE_MINDMAP_NODES', getAPIMethod('WorkspaceMindmapNode', 'deleteAll')),
            delete: networkAction('DELETE_WORKSPACE_MINDMAP_NODE', getAPIMethod('WorkspaceMindmapNode', 'delete')),
            voidAll: action('VOID_ALL_WORKSPACE_MINDMAP_NODES')
        }),
        editAllNodesCustom: (parentID: number, entities: entities.WorkspaceMindmapNode[]) =>
            action('ADIT_ALL_WORKSPACE_MINDMAP_NODE_CUSTOMS', { parentID, entities })
    };

interface GroupCollaborationRestorePointOptions {
    entityType?: GroupCollaborationEntityType | null;
    owner?: GroupCollaborationEntityOwner;
    templateFilter?: GroupCollaborationTemplateFilter;
    workshopDuration?: GroupCollaborationWorkshopDuration;
}

export const setGroupCollaborationRestorePoint = (path: string, options: GroupCollaborationRestorePointOptions = {}) =>
    action('SET_GROUP_COLLABORATION_RESTORE_POINT', { path, options });

export type WorkshopAction = ExtractAction<
| typeof loadAllSystemWorkshopTemplates
| typeof workshops
| typeof workshopBackgroundImages
| typeof workshopParticipantRoles
| typeof workshopParticipants
| typeof workshopVideoCalls
| typeof workshopPhases
| typeof workshopPhaseParticipantRoles
| typeof loadAllWorkshopPhaseContentRowLayouts
| typeof workshopPhaseContentRows
| typeof loadAllWorkshopCanvasItemTypes
| typeof workshopCanvasItems
| typeof loadAllWorkshopWidgetTypes
| typeof loadAllWorkshopWidgetStatuses
| typeof loadAllWorkshopExternalContentTypes
| typeof loadAllWorkshopPhaseContentCreationRules
| typeof workshopWidgets
| typeof workshopWidgetParticipantStatuses
| typeof workshopItemCategories
| typeof workshopItemCategoryConnections
| typeof loadAllWorkshopPrioritizationVotingTypes
| typeof workshopQuestionnaireFilters
| typeof loadAllWorkshopQuestionnaireQuestionTypes
| typeof workshopQuestionnaireQuestions
| typeof workshopQuestionnaireOptions
| typeof workshopQuestionnaireResponses
| typeof loadAllWorkshopResultPresentationTypes
| typeof workshopResultPhaseSources
| typeof workshopItems
| typeof loadAllWorkshopVersionHistoryChangeTypes
| typeof loadWorkshopItemHistory
| typeof loadAllWorkshopRatingPhaseWeightedLevels
| typeof workshopRatingPhaseSources
| typeof workshopRatingPhaseCategoryWeights
| typeof workshopItemRatings
| typeof loadWorkshopItemRatingHistory
| typeof workshopPhaseGridBlocks
| typeof workshopPhaseComparisonAxes
| typeof workshopPhaseComparisonDimensions
| typeof workshopWidgetIdeaGenerationThemes
| typeof workspaceMindmaps
| typeof workspaceMindmapNodeTypes
| typeof workspaceMindmapNodes
| typeof setGroupCollaborationRestorePoint
>;
